// ============================================================
// useCurrentUser
// ------------------------------------------------------------
// Resolves the logged-in user's display details by combining the
// JWT (sync, always available) with the production
// `GET /api/v1/user?email=` endpoint (firstname, lastname, roles).
//
// The JWT in production does NOT carry firstname/lastname, so the
// header used to fall back to "Account". We now hydrate from the
// users API so the header matches portal.smplify.com.
// ============================================================
import { useQuery } from "@tanstack/react-query";
import { getCurrentUserEmail, getCurrentUserName } from "@/lib/auth";
import { getUserByEmail } from "@/services/userManagementApi";
import type { UserManagementDTO } from "@/types/api";

export interface CurrentUserDisplay {
  email: string | null;
  /** Best-available display name: API firstname + lastname → JWT name → email local-part. */
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  /** All role names, uppercased like the production header (e.g. "DEVICE_USER, TENANT_ADMIN"). */
  roles: string[];
  isLoading: boolean;
}

function roleNames(user: UserManagementDTO | undefined): string[] {
  const raw = (user?.roles ?? []) as Array<string | { name?: string }>;
  return raw
    .map((r) => (typeof r === "string" ? r : r?.name ?? ""))
    .filter(Boolean);
}

export function useCurrentUser(): CurrentUserDisplay {
  const email = getCurrentUserEmail();

  const { data, isLoading } = useQuery({
    queryKey: ["current-user", email],
    queryFn: () => getUserByEmail(email!),
    enabled: !!email,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const firstName = data?.firstname ?? null;
  const lastName = data?.lastname ?? null;
  const fullFromApi = [firstName, lastName].filter(Boolean).join(" ").trim();
  const jwtName = getCurrentUserName();
  const localPart = email?.split("@")[0] ?? null;

  const displayName = fullFromApi || jwtName || localPart;

  return {
    email,
    displayName,
    firstName,
    lastName,
    roles: roleNames(data),
    isLoading: !!email && isLoading,
  };
}
